import type { CSSProperties, ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Tooltip } from "react-tooltip";
import { v4 as uuidv4 } from "uuid";

interface ButtonProps {
    /**
     * The button name identifier.
     */
    name?: string;
    /**
     * Whether the button is hidden.
     */
    hidden?: boolean;
    /**
     * Whether the button is disabled.
     */
    disabled?: boolean;
    /**
     * Whether the button is in the selected state.
     */
    selected?: boolean;
    /**
     * The click event handler.
     */
    onClick?: () => void;
    /**
     * Additional CSS class name.
     */
    className?: string;
    /**
     * The icon color.
     */
    color?: string;
    /**
     * The button title text.
     */
    title?: string;
    /**
     * Inline styles for the button.
     */
    style?: CSSProperties;
    /**
     * The tooltip text.
     */
    tooltip?: string;
    /**
     * The function that renders the button icon.
     */
    renderIcon?: (color: string) => ReactNode;
}

/**
 * Base toolbar button with tooltip, disabled/selected state, and icon render slot.
 *
 * @param {ButtonProps} props - The component props.
 * @returns The rendered toolbar button.
 */
const Button = ({
    name,
    hidden,
    disabled,
    selected,
    onClick,
    className = "",
    color: colorProp,
    title,
    style = {},
    tooltip,
    renderIcon,
}: ButtonProps): ReactNode => {
    const isDisabled = disabled || !onClick;

    const color = useMemo(() => {
        if (colorProp) {
            return colorProp;
        }
        if (selected) {
            return "#fff";
        }
        if (isDisabled) {
            return "#bbb";
        }
        return "#485865";
    }, [colorProp, selected, isDisabled]);

    const handleClick = useCallback(() => {
        if (isDisabled) {
            return;
        }
        onClick?.();
    }, [isDisabled, onClick]);

    const buttonClassName = `${selected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`;
    const tooltipId = useMemo(() => uuidv4(), []);

    return (
        <span
            data-tooltip-id={tooltipId}
            data-tooltip-content={tooltip}
            data-tooltip-place="right"
        >
            <Tooltip id={tooltipId} />
            <div
                key={name}
                className={`button ${buttonClassName} ${className}`}
                style={{
                    color: color,
                    display: !hidden ? "" : "none",
                    ...style,
                }}
                onClick={handleClick}
            >
                <div className="iconButton">{renderIcon?.(color)}</div>
                {title && <div className="nameButton">{title}</div>}
            </div>
        </span>
    );
};

export default Button;
